import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Clock, MapPin, CheckCircle2, Loader2, Heart, Users, Sparkles, UtensilsCrossed, FileText, Send } from "lucide-react";
import { useContent } from "@/lib/content";
import { getJobs, submitApplication } from "@/lib/api";
import { Overline } from "@/components/Overline";
import { Seo } from "@/components/Seo";

const ease = [0.16, 1, 0.3, 1];
const EMPLOYMENT = ["Vollzeit", "Teilzeit", "Minijob", "Ausbildung"];
const STANDARD_POSITIONS = ["Service", "Küche", "Pizzabäcker:in", "Spülkraft", "Initiativbewerbung"];

const BENEFITS = [
  { icon: Heart, title: "Familiäres Team", text: "Seit 1998 arbeiten wir Seite an Seite – mit Respekt, Humor und echtem Zusammenhalt." },
  { icon: Sparkles, title: "Faire Bedingungen", text: "Pünktliche Bezahlung, geregelte Arbeitszeiten und Trinkgeldbeteiligung." },
  { icon: UtensilsCrossed, title: "Gutes Essen", text: "Frische italienische Verpflegung während der Schicht – natürlich gratis." },
  { icon: Users, title: "Entwicklung", text: "Wir fördern Talente – ob Ausbildung, Aufstieg oder neue Aufgaben." },
];

const initialForm = {
  first_name: "", last_name: "", phone: "", email: "", position: "",
  employment_type: "", earliest_start: "", experience: "", languages: "",
  drivers_license: false, motivation: "", consent: false,
};

export default function Career() {
  const c = useContent();
  const [jobs, setJobs] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [files, setFiles] = useState({ cv: null, certificates: null, photo: null });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { getJobs().then(setJobs).catch(() => setJobs([])); }, []);

  const positions = [...new Set([...jobs.map((j) => j.title), ...STANDARD_POSITIONS])];
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Bitte stimmen Sie der Datenschutzerklärung zu."); return; }
    if (!files.cv) { toast.error("Bitte laden Sie Ihren Lebenslauf (PDF) hoch."); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (files.cv) fd.append("cv", files.cv);
      if (files.certificates) fd.append("certificates", files.certificates);
      if (files.photo) fd.append("photo", files.photo);
      await submitApplication(fd);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bewerbung konnte nicht gesendet werden.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-white border border-wine/15 rounded px-3 py-2.5 text-sm focus:border-wine focus:outline-none";
  const labelCls = "text-xs tracking-[0.12em] uppercase text-ink-muted mb-1.5 block";

  return (
    <div data-testid="career-page" className="bg-cream">
      <Seo page="career" />
      <section className={`pt-44 pb-16 relative ${c.images.careerHero ? "" : "bg-sand grain"}`}>
        {c.images.careerHero && (
          <>
            <div className="absolute inset-0"><img src={c.images.careerHero} alt="" className="w-full h-full object-cover" /></div>
            <div className="absolute inset-0 bg-ink/60" />
          </>
        )}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Overline light={!!c.images.careerHero} className="!flex justify-center">Karriere</Overline>
          <h1 className={`font-serif font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight ${c.images.careerHero ? "text-cream" : "text-ink"}`}>{c.text.career.heroTitle}</h1>
          <p className={`font-light mt-5 max-w-2xl mx-auto ${c.images.careerHero ? "text-cream/85" : "text-ink-soft"}`}>
            {c.text.career.heroText}
          </p>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {BENEFITS.map((b, i) => (
              <motion.div key={b.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08, ease }} className="text-center sm:text-left">
                <b.icon className="mx-auto sm:mx-0 text-wine mb-4" size={30} strokeWidth={1.3} />
                <h3 className="font-serif text-2xl text-ink mb-2">{b.title}</h3>
                <p className="text-ink-soft font-light text-sm leading-relaxed">{b.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM PHOTOS (if any) */}
      {Array.isArray(c.team) && c.team.filter((m) => m.image).length > 0 && (
        <section className="py-16 bg-sand grain">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <Overline className="!flex justify-center">Unser Team</Overline>
              <h2 className="font-serif font-light text-3xl sm:text-4xl text-ink">Die Menschen hinter La Contadina</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {c.team.filter((m) => m.image).map((m, i) => (
                <div key={i} className="text-center">
                  <div className="overflow-hidden rounded-lg mb-3"><img src={m.image} alt={m.name} className="w-full h-56 object-cover" /></div>
                  <p className="font-serif text-xl text-ink">{m.name}</p>
                  {m.role && <p className="text-xs uppercase tracking-[0.15em] text-ink-muted mt-1">{m.role}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* OPEN POSITIONS */}
      <section className="py-20 md:py-28" data-testid="open-positions">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <Overline className="!flex justify-center">Offene Stellen</Overline>
            <h2 className="font-serif font-light text-3xl sm:text-4xl text-ink">Aktuell suchen wir</h2>
          </div>
          {jobs.length === 0 ? (
            <p className="text-center text-ink-soft font-light">Aktuell sind keine Stellen ausgeschrieben – wir freuen uns trotzdem über Ihre <strong>Initiativbewerbung</strong> unten.</p>
          ) : (
            <div className="space-y-4">
              {jobs.map((j) => (
                <motion.div key={j.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, ease }} className="bg-white border border-gold/20 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid={`job-${j.id}`}>
                  <div>
                    <h3 className="font-serif text-2xl text-ink">{j.title}</h3>
                    {j.description && <p className="text-ink-soft font-light text-sm mt-1">{j.description}</p>}
                    <div className="flex flex-wrap gap-4 mt-3 text-xs text-ink-muted">
                      <span className="inline-flex items-center gap-1.5"><Clock size={14} className="text-gold" />{j.employment_type}</span>
                      <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-gold" />{j.location}</span>
                    </div>
                  </div>
                  <a href="#bewerben" onClick={() => set("position", j.title)} className="btn-pop bg-wine text-cream hover:bg-wine-dark px-6 py-3 uppercase text-[11px] tracking-[0.2em] whitespace-nowrap text-center">Bewerben</a>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section id="bewerben" className="py-20 md:py-28 bg-sand grain scroll-mt-28">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {done ? (
            <div className="bg-white border border-gold/20 p-10 text-center" data-testid="application-success">
              <CheckCircle2 className="mx-auto text-green-600 mb-5" size={56} strokeWidth={1.3} />
              <h2 className="font-serif font-light text-3xl text-ink mb-3">Vielen Dank für Ihre Bewerbung!</h2>
              <p className="text-ink-soft font-light">Wir haben Ihre Unterlagen erhalten und melden uns zeitnah bei Ihnen. Wir freuen uns, Sie kennenzulernen!</p>
            </div>
          ) : (
            <>
              <div className="text-center mb-10">
                <Overline className="!flex justify-center">Bewerbung</Overline>
                <h2 className="font-serif font-light text-3xl sm:text-4xl text-ink">Jetzt bewerben</h2>
                <p className="text-ink-soft font-light mt-3">Füllen Sie das Formular aus – ganz ohne E-Mail. Wir freuen uns auf Sie!</p>
              </div>
              <form onSubmit={onSubmit} className="bg-white border border-gold/20 p-6 sm:p-10 space-y-6" data-testid="application-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div><label className={labelCls}>Vorname *</label><input data-testid="app-firstname" required value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className={inputCls} /></div>
                  <div><label className={labelCls}>Nachname *</label><input data-testid="app-lastname" required value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className={inputCls} /></div>
                  <div><label className={labelCls}>Telefon *</label><input data-testid="app-phone" required value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} /></div>
                  <div><label className={labelCls}>E-Mail *</label><input data-testid="app-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} /></div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelCls}>Gewünschte Position *</label>
                    <select data-testid="app-position" required value={form.position} onChange={(e) => set("position", e.target.value)} className={inputCls}>
                      <option value="">Bitte wählen…</option>
                      {positions.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Beschäftigung</label>
                    <select data-testid="app-employment" value={form.employment_type} onChange={(e) => set("employment_type", e.target.value)} className={inputCls}>
                      <option value="">Bitte wählen…</option>
                      {EMPLOYMENT.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div><label className={labelCls}>Frühester Eintrittstermin</label><input data-testid="app-start" type="date" value={form.earliest_start} onChange={(e) => set("earliest_start", e.target.value)} className={inputCls} /></div>
                  <div><label className={labelCls}>Sprachkenntnisse</label><input data-testid="app-languages" placeholder="z. B. Deutsch, Italienisch, Englisch" value={form.languages} onChange={(e) => set("languages", e.target.value)} className={inputCls} /></div>
                </div>

                <div><label className={labelCls}>Berufserfahrung</label><textarea data-testid="app-experience" rows={3} value={form.experience} onChange={(e) => set("experience", e.target.value)} className={inputCls + " resize-y"} placeholder="Kurz zu Ihrer bisherigen Erfahrung" /></div>
                <div><label className={labelCls}>Kurzes Motivationsschreiben</label><textarea data-testid="app-motivation" rows={4} value={form.motivation} onChange={(e) => set("motivation", e.target.value)} className={inputCls + " resize-y"} placeholder="Warum möchten Sie bei uns arbeiten?" /></div>

                <label className="flex items-center gap-2 text-sm text-ink-soft">
                  <input data-testid="app-license" type="checkbox" checked={form.drivers_license} onChange={(e) => set("drivers_license", e.target.checked)} /> Ich besitze einen Führerschein
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 pt-2">
                  <FileField label="Lebenslauf (PDF) *" accept="application/pdf" testid="app-cv" file={files.cv} onChange={(f) => setFiles((p) => ({ ...p, cv: f }))} />
                  <FileField label="Zeugnisse (optional)" accept="application/pdf,image/*" testid="app-certs" file={files.certificates} onChange={(f) => setFiles((p) => ({ ...p, certificates: f }))} />
                  <FileField label="Foto (optional)" accept="image/*" testid="app-photo" file={files.photo} onChange={(f) => setFiles((p) => ({ ...p, photo: f }))} />
                </div>

                <label className="flex items-start gap-2 text-sm text-ink-soft">
                  <input data-testid="app-consent" type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1" />
                  <span>Ich habe die <a href="/datenschutz" target="_blank" className="text-wine underline">Datenschutzerklärung</a> gelesen und stimme der Verarbeitung meiner Daten zum Zweck der Bewerbung zu. *</span>
                </label>

                <button type="submit" disabled={submitting} data-testid="app-submit" className="btn-pop w-full bg-wine text-cream hover:bg-wine-dark px-8 py-4 uppercase text-xs tracking-[0.25em] inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  {submitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />} Bewerbung absenden
                </button>
              </form>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

const FileField = ({ label, accept, testid, file, onChange }) => (
  <div>
    <label className="text-xs tracking-[0.12em] uppercase text-ink-muted mb-1.5 block">{label}</label>
    <label className="cursor-pointer flex items-center gap-2 bg-cream border border-wine/20 rounded px-3 py-2.5 text-sm text-ink-soft hover:border-wine transition-colors">
      <FileText size={16} className="text-wine shrink-0" />
      <span className="truncate">{file ? file.name : "Datei wählen"}</span>
      <input type="file" accept={accept} data-testid={testid} className="hidden" onChange={(e) => onChange(e.target.files?.[0] || null)} />
    </label>
  </div>
);
